/**
 * DateRangePicker Component
 * A reusable date range picker with presets and calendar selection
 * Based on: https://github.com/johnpolacek/date-range-picker-for-shadcn
 */

import { Button } from '@shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { Calendar } from '@shared/components/ui/shadcn/calendar';
import { cn } from '@shared/utils/utils';
import { endOfMonth, format, isAfter, isBefore, startOfMonth, startOfYear, subDays, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { DateInput } from './DateInput';

// Preset date ranges
const PRESETS = [
  { name: 'Hoy', value: 0 },
  { name: 'Ayer', value: 1 },
  { name: 'Últimos 7 días', value: 7 },
  { name: 'Últimos 14 días', value: 14 },
  { name: 'Últimos 30 días', value: 30 },
  { name: 'Últimos 90 días', value: 90 },
  { name: 'Este mes', value: 'thisMonth' },
  { name: 'Mes pasado', value: 'lastMonth' },
  { name: 'Este año', value: 'thisYear' },
] as const;

type PresetValue = number | 'thisMonth' | 'lastMonth' | 'thisYear';

interface DateRangePickerProps {
  /** Initial start date */
  initialDateFrom?: Date | string;
  /** Initial end date */
  initialDateTo?: Date | string;
  /** Callback when date range is updated */
  onUpdate?: (values: { range: DateRange }) => void;
  /** Alignment of the popover */
  align?: 'start' | 'center' | 'end';
  /** Show preset options */
  showPresets?: boolean;
  /** Trigger button className */
  triggerClassName?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Data test id */
  dataTestId?: string;
}

const getDateFromPreset = (preset: PresetValue): { from: Date; to: Date } => {
  const now = new Date();
  // Mediodía local: evita que "Hoy" se muestre como ayer en zonas UTC- (p. ej. GMT-3).
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);

  const setNoon = (d: Date) => {
    const copy = new Date(d);
    copy.setHours(12, 0, 0, 0);
    return copy;
  };

  if (typeof preset === 'number') {
    if (preset === 0) {
      return { from: today, to: today };
    }
    if (preset === 1) {
      const yesterday = subDays(today, 1);
      return { from: setNoon(yesterday), to: setNoon(yesterday) };
    }
    return { from: setNoon(subDays(today, preset - 1)), to: today };
  }

  switch (preset) {
    case 'thisMonth':
      return { from: setNoon(startOfMonth(today)), to: today };
    case 'lastMonth': {
      const lastMonth = subMonths(today, 1);
      return { from: setNoon(startOfMonth(lastMonth)), to: setNoon(endOfMonth(lastMonth)) };
    }
    case 'thisYear':
      return { from: setNoon(startOfYear(today)), to: today };
    default:
      return { from: setNoon(subDays(today, 30)), to: today };
  }
};

export const DateRangePicker = ({
  initialDateFrom,
  initialDateTo,
  onUpdate,
  align = 'end',
  showPresets = true,
  triggerClassName,
  placeholder = 'Seleccionar rango de fechas',
  dataTestId = 'date-range-picker',
}: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLeftCalendar, setShowLeftCalendar] = useState(true);
  const skipCloseNotifyRef = useRef(false);

  // Parsea fechas: Y-m-d como local para evitar desfase de un día por UTC
  const parseDate = (date: Date | string | undefined): Date | undefined => {
    if (!date) return undefined;
    if (date instanceof Date) return date;
    const str = typeof date === 'string' ? date.trim() : String(date);
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, y, m, d] = match.map(Number);
      const parsed = new Date(y, m - 1, d, 12, 0, 0, 0);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const [range, setRange] = useState<DateRange>({
    from: parseDate(initialDateFrom),
    to: parseDate(initialDateTo),
  });

  // Track which date we're selecting (for mobile/sequential selection)
  const [selectingEnd, setSelectingEnd] = useState(false);

  // Sync with external props (including when cleared)
  useEffect(() => {
    const newFrom = parseDate(initialDateFrom);
    const newTo = parseDate(initialDateTo);
    setRange({ from: newFrom, to: newTo });
  }, [initialDateFrom, initialDateTo]);

  const notifyParentIfComplete = (next: DateRange) => {
    if (next.from && next.to) {
      onUpdate?.({ range: next });
    }
  };

  // Handle calendar selection
  const handleCalendarSelect = (newRange: DateRange | undefined) => {
    if (!newRange) return;

    // If we're selecting the first date
    if (!selectingEnd) {
      setRange({ from: newRange.from, to: undefined });
      setSelectingEnd(true);
      return;
    }

    // Selecting second date
    let next: DateRange = range;
    if (newRange.from && newRange.to) {
      next = isAfter(newRange.from, newRange.to) ? { from: newRange.to, to: newRange.from } : newRange;
    } else if (newRange.from) {
      const firstDate = range.from!;
      const secondDate = newRange.from;
      next = isBefore(secondDate, firstDate)
        ? { from: secondDate, to: firstDate }
        : { from: firstDate, to: secondDate };
    }
    setRange(next);
    setSelectingEnd(false);
    notifyParentIfComplete(next);
  };

  // Handle preset selection
  const handlePresetSelect = (preset: PresetValue) => {
    const { from, to } = getDateFromPreset(preset);
    const next = { from, to };
    setRange(next);
    setSelectingEnd(false);
    notifyParentIfComplete(next);
  };

  // Handle date input changes
  const handleFromDateChange = (date: Date) => {
    const next = range.to && isAfter(date, range.to) ? { from: date, to: date } : { ...range, from: date };
    setRange(next);
    notifyParentIfComplete(next);
  };

  const handleToDateChange = (date: Date) => {
    const next = range.from && isBefore(date, range.from) ? { from: date, to: range.from } : { ...range, to: date };
    setRange(next);
    notifyParentIfComplete(next);
  };

  // Apply the selection and close
  const handleApply = () => {
    if (range.from && range.to) {
      onUpdate?.({ range });
      setIsOpen(false);
      setSelectingEnd(false);
    }
  };

  // Cancel and reset
  const handleCancel = () => {
    skipCloseNotifyRef.current = true;
    setRange({
      from: parseDate(initialDateFrom),
      to: parseDate(initialDateTo),
    });
    setIsOpen(false);
    setSelectingEnd(false);
  };

  // Clear the selection
  const handleClear = () => {
    setRange({ from: undefined, to: undefined });
    onUpdate?.({ range: { from: undefined, to: undefined } });
    setIsOpen(false);
    setSelectingEnd(false);
  };

  // Format the display text
  const formatDisplayText = () => {
    if (!range.from) return placeholder;
    if (!range.to) return format(range.from, 'dd MMM yyyy', { locale: es });
    return `${format(range.from, 'dd MMM yyyy', { locale: es })} - ${format(range.to, 'dd MMM yyyy', { locale: es })}`;
  };

  // Calculate the month to display
  const getDisplayMonth = () => {
    if (showLeftCalendar) {
      return range.from || new Date();
    }
    const baseDate = range.to || range.from || new Date();
    return baseDate;
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (!skipCloseNotifyRef.current) {
            notifyParentIfComplete(range);
          }
          skipCloseNotifyRef.current = false;
          setSelectingEnd(false);
        }
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal h-10',
            !range.from && 'text-muted-foreground',
            triggerClassName,
          )}
          data-testid={dataTestId}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDisplayText()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 min-w-[480px] max-h-[var(--radix-popover-content-available-height)] bg-card-bg text-foreground border-card-border flex flex-col"
        align={align}
        collisionPadding={12}
      >
        <div className="flex flex-col sm:flex-row overflow-y-auto flex-1 min-h-0">
          {/* Presets sidebar - colores semánticos para dark/light */}
          {showPresets && (
            <div className="flex flex-col border-r border-card-border p-3 sm:w-[160px] bg-card-bg text-foreground shrink-0">
              <p className="text-xs font-medium text-muted-foreground mb-2 px-2">Rangos rápidos</p>
              {PRESETS.map((preset) => (
                <Button
                  key={preset.name}
                  variant="ghost"
                  size="sm"
                  className="justify-start font-normal h-8 text-sm text-foreground hover:bg-hover-press hover:text-foreground"
                  onClick={() => handlePresetSelect(preset.value)}
                >
                  {preset.name}
                </Button>
              ))}
            </div>
          )}

          {/* Calendar section */}
          <div className="p-4 min-w-[320px] bg-card-bg text-foreground">
            {/* Date inputs */}
            <div className="flex items-center gap-3 mb-4 shrink-0">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Desde</span>
                {range.from ? (
                  <DateInput value={range.from} onChange={handleFromDateChange} />
                ) : (
                  <div className="h-8 w-[120px] flex items-center justify-center border border-dashed border-card-border rounded-md text-xs text-muted-foreground bg-background">
                    Seleccionar
                  </div>
                )}
              </div>
              <span className="text-muted-foreground mt-4">→</span>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Hasta</span>
                {range.to ? (
                  <DateInput value={range.to} onChange={handleToDateChange} />
                ) : (
                  <div className="h-8 w-[120px] flex items-center justify-center border border-dashed border-card-border rounded-md text-xs text-muted-foreground bg-background">
                    Seleccionar
                  </div>
                )}
              </div>
            </div>

            {/* Calendar navigation for mobile */}
            <div className="flex items-center justify-between mb-2 sm:hidden shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLeftCalendar(true)}
                className={cn(showLeftCalendar && 'bg-accent')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">{format(getDisplayMonth(), 'MMMM yyyy', { locale: es })}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLeftCalendar(false)}
                className={cn(!showLeftCalendar && 'bg-accent')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Calendar */}
            <div>
              <Calendar
                mode="range"
                selected={range}
                onSelect={handleCalendarSelect}
                numberOfMonths={1}
                defaultMonth={range.from}
                className="rounded-md"
              />
            </div>
          </div>
        </div>

        {/* Action buttons - sticky at bottom */}
        <div className="flex items-center justify-between p-4 pt-3 border-t border-card-border bg-card-bg shrink-0">
          {/* Limpiar - solo si hay fechas */}
          <div>
            {(range.from || range.to || initialDateFrom || initialDateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="text-muted-foreground hover:text-destructive"
              >
                Limpiar
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleApply} disabled={!range.from || !range.to} className="gap-1">
              <Check className="h-4 w-4" />
              Aplicar
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
